import type { IBaseAttributes, Model } from '@feathr/rachis';

import type { IAddress } from '../address';
import type { EIntegrationTypes } from '../integrations';
import type { IPredicate, IStoredPredicate } from '../segments';
import type { IAggregatedStats } from '../stats_aggregated';
import type { TAffinityCampaign } from './affinity';
import type { TConversationCampaign } from './conversation';
import type { TEmailListCampaign } from './email_list';
import type { TFacebookCampaign } from './facebook';
import type { TLandingPageCampaign } from './landing_page';
import type { TLookalikeCampaign } from './lookalike';
import type { TMobileGeoFenceRetargetingCampaign } from './mobile_geo_fence_retargeting';
import type { TMobileGeoFencingCampaign } from './mobile_geo_fencing';
import type { IMonetizationAttributes } from './monetization';
import type { TReferralCampaign } from './referral';
import type { TSearchCampaign } from './search';
import type { TSeedSegmentCampaign } from './seed_segment';
import type { ISegmentCampaignAttributes } from './segment';
import type { TTrackedLinkCampaign } from './tracked_link';

export interface INextStep {
  cta: string;
  ctaUrl: string;
  message: string;
}

export enum CampaignState {
  Draft = 'draft',
  Erroring = 'erroring',
  Complete = 'complete',
  Completing = 'completing',
  Published = 'published',
  Publishing = 'publishing',
  Scheduled = 'scheduled',
  Stopped = 'stopped',
}

// TODO: Update TTD campaigns to use this enum when the backend is updated
export enum ETTDCampaignClass {
  Affinity = 'Campaign.DisplayCampaign.RTBCampaign.AffinityCampaign',
  Lookalike = 'Campaign.DisplayCampaign.RTBCampaign.LookalikeCampaign',
  EmailList = 'Campaign.DisplayCampaign.RTBCampaign.EmailMappingCampaign',
  MobileGeoFenceRetargeting = 'Campaign.DisplayCampaign.RTBCampaign.MobileGeofenceRetargetingCampaign',
  MobileGeoFencing = 'Campaign.DisplayCampaign.RTBCampaign.MobileGeofencingCampaign',
  Search = 'Campaign.DisplayCampaign.RTBCampaign.SearchCampaign',
  SeedSegment = 'Campaign.DisplayCampaign.RTBCampaign.SeedSegmentCampaign',
  Segment = 'Campaign.DisplayCampaign.RTBCampaign.SegmentCampaign',
  Omni = 'Campaign.DisplayCampaign.RTBCampaign.OmniCampaign',
}

export enum CampaignClass {
  Affinity = 'Campaign.DisplayCampaign.RTBCampaign.AffinityCampaign',
  AutoPinpointEmail = 'Campaign.SESBaseEmailCampaign.PinpointEmailCampaign.SmartPinpointEmailCampaign.AutoPinpointEmailCampaign',
  Conversation = 'Campaign.ConversationCampaign',
  Drip = 'Campaign.DripCampaign',
  DripStep = 'Campaign.SESBaseEmailCampaign.PinpointEmailCampaign.SmartPinpointEmailCampaign.AutoPinpointEmailCampaign.DripCampaignStep',
  EmailList = 'Campaign.DisplayCampaign.RTBCampaign.EmailMappingCampaign',
  /** @deprecated Use CampaignClass.Omni with EIntegrationTypes.Meta instead. */
  EmailListFacebook = 'Campaign.DisplayCampaign.RTBCampaign.FacebookCampaign.EmailListFacebookCampaign',
  /** @deprecated Use CampaignClass.Omni with EIntegrationTypes.Meta instead. */
  Facebook = 'Campaign.DisplayCampaign.RTBCampaign.FacebookCampaign',
  GoogleAdsSearch = 'Campaign.DisplayCampaign.RTBCampaign.GoogleCampaign.GoogleAdsSearchCampaign',
  GoogleAdsSmart = 'Campaign.DisplayCampaign.RTBCampaign.GoogleCampaign.GoogleAdsSmartCampaign',
  LandingPage = 'Campaign.LandingPageCampaign',
  Lookalike = 'Campaign.DisplayCampaign.RTBCampaign.LookalikeCampaign',
  MailrailAutoSend = 'Campaign.SESBaseEmailCampaign.MailrailBaseEmailCampaign.MailrailAutoSendEmailCampaign',
  MailrailDrip = 'Campaign.SESBaseEmailCampaign.MailrailBaseEmailCampaign.MailrailAutoSendEmailCampaign.MailrailDripEmailCampaign',
  MailrailPartnerMessage = 'Campaign.SESBaseEmailCampaign.MailrailBaseEmailCampaign.MailrailSingleSendEmailCampaign.MailrailPartnerMessage',
  MailrailSingleSend = 'Campaign.SESBaseEmailCampaign.MailrailBaseEmailCampaign.MailrailSingleSendEmailCampaign',
  MobileGeoFenceRetargeting = 'Campaign.DisplayCampaign.RTBCampaign.MobileGeofenceRetargetingCampaign',
  MobileGeoFencing = 'Campaign.DisplayCampaign.RTBCampaign.MobileGeofencingCampaign',
  Omni = 'Campaign.DisplayCampaign.RTBCampaign.OmniCampaign',
  PinpointEmail = 'Campaign.SESBaseEmailCampaign.PinpointEmailCampaign',
  PinpointPartnerMessage = 'Campaign.SESBaseEmailCampaign.PinpointEmailCampaign.PinpointPartnerMessage',
  Referral = 'Campaign.ReferralCampaign',
  Search = 'Campaign.DisplayCampaign.RTBCampaign.SearchCampaign',
  SeedSegment = 'Campaign.DisplayCampaign.RTBCampaign.SeedSegmentCampaign',
  Segment = 'Campaign.DisplayCampaign.RTBCampaign.SegmentCampaign',
  SmartPinpointEmail = 'Campaign.SESBaseEmailCampaign.PinpointEmailCampaign.SmartPinpointEmailCampaign',
  TrackedLink = 'Campaign.TrackedLinkCampaign',
}

export interface ICampaignSyncProgress {
  synced_campaign: boolean;
  synced_adgroup: boolean;
  synced_audience: boolean;
  synced_targetings: boolean;
  synced_creatives: boolean;
  built_proximity: boolean;
  applied_proximity: boolean;
  uploaded_emaillist: boolean;
}

enum ELinkedCampaignKind {
  autosend_existing = 'autosend_existing',
  drip_campaign_step = 'drip_campaign_step',
}

interface ILinkedCampaign {
  kind: ELinkedCampaignKind;
  /** campaign id */
  campaign: string;
}

export interface ICampaignAttributes extends IBaseAttributes {
  readonly _cls: CampaignClass;
  /** User id */
  readonly created_by: string;
  readonly event: string;
  readonly parent: string;
  readonly parent_kind: string;
  readonly sync_progress: ICampaignSyncProgress;
  alt_text?: string;
  cloned_from?: string;
  conversion_tracking_mode: 'auto' | 'advanced';
  date_created: string;
  date_start: string;
  date_end: string;
  /** Timestamp the campaign was first published to its ad platform; null until first publish. */
  date_first_published?: string | null;
  date_last_modified: string;
  exposure_settings: IExposureSettings;
  integrations?: EIntegrationTypes[];
  linked_campaigns: ILinkedCampaign[];
  flight?: string;
  is_enabled: boolean;
  name: string;
  rerun: boolean;
  state: CampaignState;
  status_messages?: string[];
  subadvertiser?: string;
  stats_cache?: IAggregatedStats;
  show_bot_toggle?: boolean;
}

export interface IMergeField {
  name: string;
  type: string;
  value: string;
  pinpointValue?: string;
  noDefault?: boolean;
  isCustom?: boolean;
}

export type TCampaignGroup = 'ads' | 'all' | 'email' | 'monetization' | 'other' | 'google-ads';
export type TAdChannelKey = 'tradedesk' | 'meta';
/**
 * A key into a stats `channels` map. Either an ad-channel key (`tradedesk`/`meta`) or, for
 * Mailrail campaigns, an email's `mailing_id`. The `string & NonNullable<unknown>` intersection
 * preserves `tradedesk`/`meta` editor autocomplete while still allowing any string.
 */
export type TChannelKey = TAdChannelKey | (string & NonNullable<unknown>);
type ExposureSettingsTargetType = 'fixed_budget' | 'fixed_impressions' | 'uncapped';
type ExposureSettingsPace = 'pace_to_end_of_day' | 'pace_to_end_of_flight' | 'pace_ahead' | 'off';

export interface IChannelAllocation {
  /** The channel identifier. */
  channel: TAdChannelKey;
  /** Integer percentage 0-100 allocated to this channel. */
  percentage: number;
}

export interface IChannelSplit {
  allocations: IChannelAllocation[];
}

// TODO: Further refine this interface to only include these settings where they are utilized
export interface IExposureSettings {
  // TTD and google use pace
  // defaulted in backend to pace_to_end_of_flight
  pace?: ExposureSettingsPace;
  // TTD and Google can use this
  recommended_target_percent?: boolean;
  // TTD use custom target
  // defaulted in backend to true - should be false
  custom_target?: boolean;
  // TTD uses target type
  // defaulted in backend to fixed_budget
  target_type?: ExposureSettingsTargetType;
  // Everything uses target value
  // defaulted in backend to 0 - should be undefined
  target_value?: number;
  /** Google Ads daily average budget */
  // TTD and Google ads use this but it will be just Google after this update
  target_cap_daily?: number;
  /** Budget split between Display and Meta channels for Omni campaigns. */
  channel_split?: IChannelSplit | null;
}

export interface IRecipientPreferencesConfig {
  account_label: string;
  button_color: string;
  button_text_color: string;
  button_text: string;
  campaign_label: string;
  description: string;
  event_label: string;
  response: string;
  title: string;
}

export interface ICampaignSegment {
  id: string;
  included: boolean;
}

export interface IAudienceSegmentIds {
  excludedSegmentIds: string[];
  includedSegmentIds: string[];
}

export type TCampaignEmailTestStatus =
  | 'pending'
  | 'integrating'
  | 'in_progress'
  | 'completed'
  | 'failed';

export interface IConsent {
  user?: string;
  has_consent?: boolean;
  date_consented?: string;
}

/**
 * Some older Pinpoint campaigns have mixed trigger kinds, from before the subtype field was
 * introduced. The "mixed" subtype allows them to be migrated to Mailrail without losing any of
 * their triggers. Publishing is blocked until the user picks a proper subtype.
 */
export type TAutosendSubtype = 'activity' | 'update' | 'time' | 'mixed';
export type TSelectableAutosendSubtype = Exclude<TAutosendSubtype, 'mixed'>;

export enum EDripTriggerAction {
  clicked_link_previous_email = 'clicked_link_previous_email',
  not_clicked_link_previous_email = 'not_clicked_link_previous_email',
  not_opened_previous_email = 'not_opened_previous_email',
  opened_previous_email = 'opened_previous_email',
  previous_email_delivered = 'previous_email_delivered',
}

export interface IDripStepCampaign extends IAutoPinpointEmailBaseCampaign {
  _cls: CampaignClass.DripStep;
  drip_campaign: string;
  set_action?: EDripTriggerAction;
  step_number: number;
}

/** Excluded because they aren't used in the auto send campaign variant drip campaigns generate */
export const dripExcludedAttributes = [
  'id',
  '_cls',
  'bidding_strategy',
  'stats_cache',
  'date_last_modified',
  'exposure_settings',
  'is_enabled',
  'parent_id',
  'rerun',
  'state',
  'segments',
] as const;

// The `as const` above is required to properly infer individual array elements
type TDripExclusions = (typeof dripExcludedAttributes)[number];

// Just the props that are added on the step specs object
export interface IDripCampaignStepSpec extends Omit<IAutoPinpointEmailCampaign, TDripExclusions> {
  set_action?: EDripTriggerAction;
}

export interface IEmailBaseCampaign extends ICampaignAttributes {
  readonly _cls:
    | CampaignClass.AutoPinpointEmail
    | CampaignClass.Drip
    | CampaignClass.DripStep
    | CampaignClass.MailrailAutoSend
    | CampaignClass.MailrailDrip
    | CampaignClass.MailrailPartnerMessage
    | CampaignClass.MailrailSingleSend
    | CampaignClass.PinpointEmail
    | CampaignClass.PinpointPartnerMessage
    | CampaignClass.SmartPinpointEmail;
  consent: IConsent;
}

/**
 * Drip campaigns are a series of emails that are sent to a person over time.
 *
 * - `actions`: The actions that are set on the first step of the drip.
 * - `date_send_end`: Calculated based on the latest end from the steps.
 * - `date_send_start`: Calculated based on the earliest start from the steps.
 * - `exit_on_conversion`: Whether person should exit the drip when they meet the campaign goal.
 * - `step_specs`: A list of blueprint objects (for building autosend campaigns) - essentially mirrors the attributes from AutoPinpointEmailCampaign.
 */
export interface IDripCampaign extends IEmailBaseCampaign {
  readonly _cls: CampaignClass.Drip;
  actions: IEmailTrigger[];
  date_send_end: string;
  date_send_start: string;
  exit_on_conversion: boolean;
  segments: ICampaignSegment[];
  send_all: boolean;
  step_specs: IDripCampaignStepSpec[];
}

export interface ISingularEmailBaseCampaign extends IEmailBaseCampaign {
  readonly _cls:
    | CampaignClass.AutoPinpointEmail
    | CampaignClass.DripStep
    | CampaignClass.MailrailAutoSend
    | CampaignClass.MailrailDrip
    | CampaignClass.MailrailPartnerMessage
    | CampaignClass.MailrailSingleSend
    | CampaignClass.PinpointEmail
    | CampaignClass.PinpointPartnerMessage
    | CampaignClass.SmartPinpointEmail;
  from_address?: string;
  from_name?: string;
  template_id?: string;
}

export interface IPinpointEmailBaseCampaign extends ISingularEmailBaseCampaign {
  readonly _cls:
    | CampaignClass.AutoPinpointEmail
    | CampaignClass.DripStep
    | CampaignClass.PinpointEmail
    | CampaignClass.SmartPinpointEmail
    | CampaignClass.PinpointPartnerMessage;
  address?: IAddress;
  date_send_end: string;
  date_send_start: string;
  endpoint_type: 'primary_email' | 'all_emails';
  filename?: string;
  preferences_config: IRecipientPreferencesConfig;
  /**
   * The content domain that "View in browser" links in emails from this campaign
   * will be served from
   */
  redirect: string;
  /** The "View in browser" link for this campaign */
  redirect_url: string;
  s3_key?: string;
  send_schedule?: 'now' | 'later' | 'dynamic';
  segments: ICampaignSegment[];
  subject?: string;
  subtype?: TSelectableAutosendSubtype;
  test_campaign_status: TCampaignEmailTestStatus;
  time_to_send?: string;
}

export interface IPinpointEmailCampaign
  extends IPinpointEmailBaseCampaign,
    IMonetizationAttributes {
  readonly _cls: CampaignClass.PinpointEmail;
}

/**
 * Pinpoint triggers can be either attribute-based or activity-based.
 * "Attribute" predicates for email campaigns work slightly differently
 * than traditional attribute predicates. We want to send an email in
 * response to a person being updated with an attribute, not target people
 * who have that attribute already. For that reason, attribute-based
 * triggers use the predicate kind "update". The exception is predicates for
 * date-triggered autosends, which do actually operate on persons' existing
 * attributes and therefore have kind "attribute".
 * Activity-based triggers use the predicate kind "activity".
 *
 * @deprecated Superseded by {@link IStoredEmailTrigger}; remove with the Pinpoint editor.
 */
export interface IEmailTrigger extends Omit<IPredicate, 'group'> {
  kind: 'update' | 'activity' | 'attribute';
}

/**
 * An {@link IEmailTrigger} for a Mailrail campaign, including {@link IStoredPredicate}'s `key`
 * field. The Pinpoint editor still uses the old FilterChip-based predicate editor that doesn't
 * populate the `key` field. This can replace IEmailTrigger once Pinpoint is removed.
 */
export interface IStoredEmailTrigger extends Omit<IStoredPredicate, 'group'> {
  kind: 'update' | 'activity' | 'attribute';
  // Email campaigns do not currently support grouped predicates
  group?: never[];
}

/**
 * Anhinga supports the following units of time for delays:
 * seconds, minutes, hours, days, and weeks. However, we only allow the user to set all of these except seconds.
 * So, for the dropdowns, use type TUnitOfTime which excludes seconds.
 */
export type TBaseUnitOfTime = 'seconds' | 'minutes' | 'hours' | 'days' | 'weeks';
export type TUnitOfTime = Exclude<TBaseUnitOfTime, 'seconds'>;

export interface ISmartPinpointEmailBaseCampaign extends IPinpointEmailBaseCampaign {
  _cls: CampaignClass.AutoPinpointEmail | CampaignClass.DripStep | CampaignClass.SmartPinpointEmail;
  actions: IEmailTrigger[];
  delay_value: number;
  delay_unit: TBaseUnitOfTime;
  mode: 'match_all' | 'match_any';
}

export interface ISmartPinpointEmailCampaign extends ISmartPinpointEmailBaseCampaign {
  readonly _cls: CampaignClass.SmartPinpointEmail;
  send_all: boolean;
}

export interface IAutoPinpointEmailBaseCampaign extends ISmartPinpointEmailBaseCampaign {
  readonly _cls: CampaignClass.AutoPinpointEmail | CampaignClass.DripStep;
  repeat: boolean;
  cooldown_value?: number;
  cooldown_unit?: TUnitOfTime;
}

export interface IAutoPinpointEmailCampaign extends IAutoPinpointEmailBaseCampaign {
  readonly _cls: CampaignClass.AutoPinpointEmail;
}

export interface IPinpointPartnerMessage extends IPinpointEmailBaseCampaign {
  readonly _cls: CampaignClass.PinpointPartnerMessage;
  invites_campaign?: string;
  participation: {
    mode: 'manual' | 'event' | 'campaign';
    partner_ids: string[];
  };
}

/**
 * A mailing is any document that can send an email, be it a full-fledged campaign, or
 * an embedded document within a campaign (such as a drip step, or an A/B test variant).
 */
export interface IEmailCampaignMailing {
  template_id?: string;
  from_address?: string;
  from_name?: string;
  contact_info: IAddress;
}

/**
 * A mailing that can be triggered by a breadcrumb, such as an autosend campaign or a drip step.
 */
export interface ITriggerableEmailCampaignMailing extends IEmailCampaignMailing {
  delay_seconds: number;
}

/**
 * An instance of a model that implements {@link IEmailCampaignMailing}. This single
 * structural type lets callers accept any such instance without a union such as
 * `Campaign | MailrailDripStep` (whose differing `get` overloads don't unify, forcing
 * casts).
 */
export type TEmailCampaignMailing = Model<IEmailCampaignMailing & IBaseAttributes>;

/**
 * A {@link TEmailCampaignMailing} that can be triggered by a breadcrumb — the structural
 * analog of {@link ITriggerableEmailCampaignMailing}.
 */
export type TTriggerableEmailCampaignMailing = Model<
  ITriggerableEmailCampaignMailing & IBaseAttributes
>;

export interface IMailrailBaseEmailCampaign
  extends ISingularEmailBaseCampaign,
    IEmailCampaignMailing {
  readonly _cls:
    | CampaignClass.MailrailAutoSend
    | CampaignClass.MailrailDrip
    | CampaignClass.MailrailPartnerMessage
    | CampaignClass.MailrailSingleSend;
  segments_excluded: string[];
  segments_included: string[];
  target_address_type: 'primary' | 'all';
}

export interface IMailrailAutoSendEmailCampaign
  extends IMailrailBaseEmailCampaign,
    ITriggerableEmailCampaignMailing {
  readonly _cls: CampaignClass.MailrailAutoSend | CampaignClass.MailrailDrip;
  bulk_send_at_end: boolean;
  bulk_send_at_start: boolean;
  predicates: IStoredEmailTrigger[];
  predicates_mode: 'match_all' | 'match_any';
  repeat_allowed: boolean;
  repeat_cooldown_seconds: number;
  subtype: TAutosendSubtype;
}

export interface IMailrailDripStep extends ITriggerableEmailCampaignMailing {
  /** Stable identifier; also the breadcrumb `ad_group` / `mailing_id` for this step. */
  step_id: string;
  name: string;
  trigger_action: EDripTriggerAction;
}

export interface IMailrailDripEmailCampaign extends IMailrailAutoSendEmailCampaign {
  readonly _cls: CampaignClass.MailrailDrip;
  /** Drips only support a start-timed (enrollment) bulk send; narrows the inherited type. */
  bulk_send_at_end: false;
  date_enrollment_end?: string;
  enrollment_step_name: string;
  exit_on_conversion: boolean;
  steps: IMailrailDripStep[];
}

export interface IMailrailSingleSendEmailCampaign extends IMailrailBaseEmailCampaign {
  readonly _cls: CampaignClass.MailrailSingleSend | CampaignClass.MailrailPartnerMessage;
}

export interface IMailrailPartnerMessage extends IMailrailSingleSendEmailCampaign {
  readonly _cls: CampaignClass.MailrailPartnerMessage;
  invites_campaign?: string;
  participation: {
    mode: 'manual' | 'event' | 'campaign';
    partner_ids: string[];
  };
}

export interface IGoogleAdsCampaign extends ICampaignAttributes {
  business_name: string;
  date_last_upsynced: string;
  date_last_downsynced: string;
  resource_name: string;
  status: number;
}

/**
 * Fields shared by every Google Ad Grants campaign. Smart and Search campaigns
 * carry an identical field set on the backend and differ only by `_cls`, so
 * both concrete interfaces narrow this shared shape.
 */
export interface IGoogleAdsGrantsFields extends IGoogleAdsCampaign {
  advertising_language_code: 'en';
  keyword_themes: IGoogleAdsKeyword[];
  phone_number_country_code?: string;
  phone_number?: string;
}

export interface IGoogleAdsSmartCampaign extends IGoogleAdsGrantsFields {
  readonly _cls: CampaignClass.GoogleAdsSmart;
}

export interface IGoogleAdsSearchCampaign extends IGoogleAdsGrantsFields {
  readonly _cls: CampaignClass.GoogleAdsSearch;
}

/** The types that can be passed to the suggest method */
export type TGoogleAdsSuggestions =
  | IGoogleAdsSuggestedAdText
  | IGoogleAdsSuggestedKeywordThemes
  | IGoogleAdsSuggestedBudgets;

export interface IGoogleAdsSuggestProps {
  type: 'budget' | 'ad' | 'keyword';
  businessName?: string;
  destinationUrl: string;
  keywordThemes?: IGoogleAdsKeyword[];
}

export interface IGoogleAdsSuggestedKeywordThemes extends IBaseAttributes {
  keyword_themes: IGoogleAdsKeyword[];
}

export interface IGoogleAdsKeyword {
  display_name: string;
  resource_name?: string;
  negative: boolean;
  suggestion: boolean;
}

/**
 * Represents the budget information for a Google Ads campaign.
 * - `daily_amount`: The daily budget amount.
 * - `monthly_amount`: The monthly budget amount calculated based on the daily amount.
 * - `min_daily_clicks`: The minimum estimated daily clicks for the budget.
 * - `max_daily_clicks`: The maximum estimated daily clicks for the budget.
 */
export interface IGoogleAdsBudget {
  daily_amount: number;
  monthly_amount: number;
  min_daily_clicks: number;
  max_daily_clicks: number;
}

export enum EGoogleAdsBudgetType {
  low = 'low',
  recommended = 'recommended',
  high = 'high',
}

type TBudgetKeys = keyof typeof EGoogleAdsBudgetType;
type TBudgetTypes = { [key in TBudgetKeys]: IGoogleAdsBudget };

/** Response for the Google Ads suggested budgets */
export interface IGoogleAdsSuggestedBudgets extends IBaseAttributes, TBudgetTypes {}

export interface IGoogleAdsAdText {
  text: string;
  key: string;
}

export interface IGoogleAdsSuggestedAdText extends IBaseAttributes {
  headlines: IGoogleAdsAdText[];
  descriptions: IGoogleAdsAdText[];
}
export interface IGoogleAdsLocationSuggestion extends IBaseAttributes {
  geo_target_constant: string;
  name: string;
  canonical_name: string;
  target_type: string;
  reach: number;
}

export type TCampaignAttributes =
  | IAutoPinpointEmailCampaign
  | IAutoPinpointEmailBaseCampaign
  | ICampaignAttributes
  | IDripCampaign
  | IDripStepCampaign
  | IEmailBaseCampaign
  | IGoogleAdsCampaign
  | IGoogleAdsSearchCampaign
  | IGoogleAdsSmartCampaign
  | IMailrailAutoSendEmailCampaign
  | IMailrailBaseEmailCampaign
  | IMailrailDripEmailCampaign
  | IMailrailPartnerMessage
  | IMailrailSingleSendEmailCampaign
  | IPinpointEmailBaseCampaign
  | IPinpointEmailCampaign
  | IPinpointPartnerMessage
  | ISegmentCampaignAttributes
  | ISingularEmailBaseCampaign
  | ISmartPinpointEmailBaseCampaign
  | ISmartPinpointEmailCampaign
  | TAffinityCampaign
  | TConversationCampaign
  | TEmailListCampaign
  | TFacebookCampaign
  | TLandingPageCampaign
  | TLookalikeCampaign
  | TMobileGeoFenceRetargetingCampaign
  | TMobileGeoFencingCampaign
  | TReferralCampaign
  | TSearchCampaign
  | TSeedSegmentCampaign
  | TTrackedLinkCampaign;
